import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation,useParams } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import "../../../styles/pages/reviewscreen.css";
import "../../../styles/pages/principleView.css";
import SendBackModal     from "../../../features/workflow/SubmissionActions/SendBackModel";
import MarkReviewedModal from "../../../features/workflow/SubmissionActions/MarkReviewedModal";
import ResponseViewRenderer from "../../../shared/components/ResponseViewRenderer";
import { getStoredRole } from "../../../shared/utils/auth";
import usePermission from "../../../shared/hooks/usePermission";
import Toast from "../../../shared/components/Toast";
import AuditTrailPanel from "../../audittrail/AuditTrailPanel";
import AuditDetailModal from "../../audittrail/AuditDetailModal";
const role = getStoredRole();


const FA_MAP = {
  "fa-submitted":    "fa-solid fa-paper-plane",
  "fa-reviewed":     "fa-solid fa-circle-check",
  "fa-approved":     "fa-solid fa-check-double",
  "fa-locked":       "fa-solid fa-lock",
  "fa-draft":        "fa-solid fa-pen-to-square",
  "fa-notstarted":   "fa-solid fa-clock",
  "fa-rework":       "fa-solid fa-rotate-left",
  "fa-sendback":     "fa-solid fa-rotate-left",
  "fa-markreviewed": "fa-solid fa-circle-check",
  "fa-view":         "fa-solid fa-eye",
  "fa-list":         "fa-solid fa-list",
  "fa-exclamation":  "fa-solid fa-circle-exclamation",
  "fa-paperclip":    "fa-solid fa-paperclip",
  "fa-calculator":   "fa-solid fa-calculator",
  "fa-database":     "fa-solid fa-database",
  "fa-flag":         "fa-solid fa-flag",
  "fa-comment":      "fa-solid fa-comment",
  "fa-comment-dots": "fa-regular fa-comment-dots",
  "fa-eye":          "fa-solid fa-eye",
  "fa-circle-check": "fa-solid fa-circle-check",
  "fa-clock":        "fa-solid fa-clock",
};

const Icon = ({ name, className: extraClass = "" }) => {
  if (!name) return null;
  const resolved = FA_MAP[name] ?? FA_MAP[`fa-${name}`] ?? "fa-solid fa-circle";
  return <i className={`${resolved} ${extraClass}`.trim()} aria-hidden="true" />;
};

// ── Stat Card ─────────────────────────────────────────────────────────────
const StatCard = ({ label, value, iconName, color }) => (
  <div className="va-stat-card">
    <div
      className="va-stat-card__icon"
      style={{ backgroundColor: `${color}18`, color }}
    >
      <Icon name={iconName} />
    </div>
    <div>
    <div className="va-stat-card__label">{label}</div>
    <div className="va-stat-card__value" style={{ color }}>{value ?? "0"}</div>
    </div>
  </div>
);

// ── Question Card ─────────────────────────────────────────────────────────
const QuestionCard = ({ q }) => {
  const isValid     = (q.validation_status ?? "").toLowerCase() === "valid";
  const isMandatory = q.is_mandatory ?? q.required ?? false;
  const isSysCal    = q.is_system_calculated ?? q.system_calculated ?? false;

  return (
    <div className="va-question">
      <div className="va-question__head">
        <div className="va-question__tags">
          <span className="va-q-code">{q.question_code}</span>
          {isMandatory && <span className="va-tag va-tag--required">Required</span>}
          {isSysCal && (
            <span className="va-tag va-tag--system">
              <Icon name="fa-calculator" /> System Calculated
            </span>
          )}
        </div>
        {isValid
          ? <span className="va-validity va-validity--valid"><Icon name="fa-circle-check" /> Valid</span>
          : <span className="va-validity va-validity--pending"><Icon name="fa-clock" /> Pending</span>
        }
      </div>
      <p className="va-question__text">{q.question_text}</p>
      <div className="va-answer-box">
        <span className="va-answer-box__label">SUBMITTED ANSWER</span>
        <div className="va-answer-box__value">
          {q.answer ?? q.display_value ?? (
            <span className="va-answer-box__empty">No answer submitted</span>
          )}
        </div>
        {q.calculated_note && (
          <div className="va-answer-box__note">
            <Icon name="fa-database" /> {q.calculated_note}
          </div>
        )}
      </div>
      <div className="va-question__foot">
        <div className="va-evidence-list">
          {(q.evidence ?? []).map((ev, i) => (
            <span key={i} className="va-evidence-chip">
              <Icon name="fa-paperclip" /> {ev.file_name}
            </span>
          ))}
        </div>
        <div className="va-question__actions">
          {q.can_mark_issue !== false && (
            <button className="va-q-action va-q-action--issue">
              <Icon name="fa-flag" /> Mark Issue
            </button>
          )}
          {q.can_request_clarification !== false && (
            <button className="va-q-action va-q-action--clarify">
              <Icon name="fa-comment" /> Request Clarification
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Accordion Section ─────────────────────────────────────────────────────
const SectionAccordion = ({ section, defaultOpen }) => {

  const [open, setOpen] = useState(defaultOpen ?? true);
  const qs = section.questions ?? [];
  return (
    <div className="va-accordion">
      <button className="va-accordion__btn" onClick={() => setOpen(p => !p)}>
        <div className="va-accordion__info">
          <span className="va-accordion__title">{section.section_name}</span>
          <span className="va-accordion__count">{qs.length} questions</span>
        </div>
        <span className={`va-accordion__chevron${open ? " va-accordion__chevron--open" : ""}`}>›</span>
      </button>
      {open && (
        <div className="va-accordion__body">
          {qs.map(q => (
            <QuestionCard key={q.question_id ?? q.question_code} q={q} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Workflow History Item ─────────────────────────────────────────────────
const HistoryItem = ({ h, isLast }) => (
  <div className="va-wf-item">
    <div className="va-wf-item__track">
      <span
        className="va-wf-item__dot"
        style={{ background: h.status_color, borderColor: h.status_fore_color, color: h.status_fore_color }}
      >
        <i className={`fa-solid ${h.status_icon ?? "fa-solid fa-circle"} workflow_history_status_icon_size`} />
      </span>
      {!isLast && <span className="va-wf-item__line" />}
    </div>
    <div className="va-wf-item__body">
      <span
        className="va-wf-item__status"
        style={{ color: h.status_fore_color }}
      >
        {h.status_name}
      </span>
      <span className="va-wf-item__meta">{h.action_by_name} · {h.action_date}</span>
    </div>
  </div>
);

// ── Comment Item ──────────────────────────────────────────────────────────
const CommentItem = ({ c }) => {
  const role = c.role_name ?? c.role ?? c.commenter_role ?? c.user_role ?? null;

  return (
    <div className="va-comment-item">
      <div className="va-comment-item__header">
        <span className="va-comment-item__author">{c.commented_by}</span>
        {role && <span className="va-comment-item__role">{role}</span>}
        <span className="va-comment-item__date">{c.comment_date}</span>
      </div>
      {c.comment_type && (
<span className={`va-q-code comment_type_${c.comment_type}`}>{c.comment_type}</span>
)}

      <p className="va-comment-item__text">
           {c.txt_status && (
            <strong>
              {c.txt_status
                .toLowerCase()
                .replace(/\b\w/g, (char) => char.toUpperCase())}:{" "}
            </strong>
          )}

        {c.comment_text}
      </p>
    </div>
  );
};


// ── Main Component ────────────────────────────────────────────────────────
const ViewAction = () => {
const [toast, setToast] = useState(null);
const { hasPermission } = usePermission();

  const navigate              = useNavigate();
  const { state: routeState } = useLocation();

 const {
  legalEntities = [],
  reportingYear,
  principle_id,
  section_name,
} = routeState ?? {};

const {
  principleCode,
  legalEntityId,
  reportingYearId,
  submissionId
} = useParams();

const submission_id = submissionId;
const selectedEntity = legalEntityId;

const [detail, setDetail] = useState(null);
const [summary, setSummary] = useState([]);
const [history, setHistory] = useState([]);
const [sections, setSections] = useState([]);
const [comments, setComments] = useState([]);
const [comment, setComment] = useState("");
const [isIssue, setIsIssue] = useState(false);
const [submitting, setSubmitting] = useState(false);
const [saved, setSaved] = useState(false);
const [loading, setLoading] = useState(true);
const [error, setError] = useState("");
const [commentAllowed, setCommentAllowed] = useState(false);
const [actions, setActions] = useState([]);
const [actionLoading, setActionLoading] = useState(null);
const [submittedResponses, setSubmittedResponses] = useState([]);
const [evidence, setEvidence] = useState([]);
const [activeModalApiUrl, setActiveModalApiUrl] = useState(null);
const [showSendBackModal, setShowSendBackModal] = useState(false);
const [auditLogs, setAuditLogs] = useState([]);
const [loadingAudit, setLoadingAudit] = useState(false);
const [selectedAudit, setSelectedAudit] = useState(null);
const [showAuditModal, setShowAuditModal] = useState(false);
const [currentPrincipleId,setCurrentPrincipleId] = useState(principle_id || null);


  const location = useLocation();
  const entityName =detail?.entity_name ||location.state?.entityName ||"";
  const sourcePage = routeState?.sourcePage;

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
const currentUserId = currentUser.id;
const isOwnSubmission = Number(detail?.submitted_by_id) === Number(currentUserId);

const formatDateTime = (dateString) => {

  if (!dateString) return "";

  const date = new Date(dateString);

  return date.toLocaleString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }
  );
};
const canComment =
  !isOwnSubmission && commentAllowed &&
  (
    hasPermission(
      "review_screen",
      "comment"
    )
    ||
    hasPermission(
      "approval_lock",
      "comment"
    )
  );
  const canReviewEvidence =
  !isOwnSubmission &&
  detail?.status_code !== "changes_requested" &&
  (
    hasPermission(
      "review_screen",
      "evidence_review"
    )
  );

  // ── Fetch submission details ──────────────────────────────────────────
  const fetchDetail = useCallback(async () => {
    if (!reportingYearId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/api/submission-details", {
        params: {
          reporting_year_id: reportingYearId,
          legal_entity_id:   selectedEntity,
          principle_id,
          submission_id,
        },
      });
      if (res.data?.success) {
        const d = res.data.data ?? {};
        setDetail(d);

        if (d.principle_id) 
          { 
            setCurrentPrincipleId(d.principle_id);
            fetchAuditTrail(d.principle_id);

              }
        setSummary(res.data.summary ?? []);
        setHistory(res.data.workflow_history ?? []);
        setEvidence(res.data.evidence ?? []);
        setCommentAllowed(!!res.data.iscomment_allowed);
        setActions(res.data.actions ?? []);
        if (Array.isArray(d.sections) && d.sections.length > 0) {
          setSections(d.sections);
        } else if (Array.isArray(d.questions)) {
          setSections([{ section_name: "Questions", questions: d.questions }]);
        } else {
          setSections([]);
        }
      } else {
        setError("Failed to load details.");
      }
    } catch (err) {
      console.error("ViewAction fetch error:", err);
      setError("Failed to load details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [reportingYearId, selectedEntity, principle_id, submission_id]);

//fetch audit trail
  const fetchAuditTrail = async (principleId) => {

  try {

    setLoadingAudit(true);

    const response =
      await apiClient.post(
        "/api/audit-trail",
        {
          selectedEntity,
          reportingYearId,
          principle_id:principleId
        }
      );

   if (response?.data?.success) {

      setAuditLogs(
        response.data.audit_logs || []
      );
}

  } catch (err) {

    console.error(
      "Audit fetch error",
      err
    );

  } finally {

    setLoadingAudit(false);
  }
};
const openAuditDetails =(log) => {
    setSelectedAudit(log);
    setShowAuditModal(true);
  };
  // ── Fetch comment history ─────────────────────────────────────────────
  const fetchComments = useCallback(async () => {
    if (!submission_id) return;
    try {
      const res = await apiClient.get("/api/getcomments", {
        params: { principle_id, submission_id },
      });
      if (res.data?.success) setComments(res.data.data ?? []);
    } catch {
      // non-blocking
    }
  }, [principle_id, submission_id]);

  // ── Fetch submitted responses ─────────────────────────────────────────
  const fetchSubmittedResponses = useCallback(async () => {
    try {
      if (!selectedEntity || !reportingYearId) return;
      const principleCode = detail?.principle_code;
      if (!principleCode) return;
      const response = await apiClient.get(
        `/api/principles/responses/${principleCode}`,
        {
          params: {
            legal_entity_id:   selectedEntity,
            reporting_year_id: reportingYearId,
          },
        }
      );
      setSubmittedResponses(response?.data?.responses || []);
    } catch (err) {
      console.error("Submitted responses fetch failed", err);
    }
  }, [detail?.principle_code, selectedEntity, reportingYearId]);

// =========================================
  // TOAST
  // =========================================

  const showToast = (
    message,
    type = "success"
  ) => {

    setToast({
      message,
      type
    });
  };

const handleEvidenceReview = async (
  evidenceId,
  reviewStatus
) => {

  try {

    const response =
      await apiClient.post(
        "/api/evidence/review",
        {
          evidence_id: evidenceId,
          review_status: reviewStatus
        }
      );

    showToast(
      response.data.message,
      "success"
    );

    await fetchDetail();

  } catch (error) {

    showToast(
      error?.response?.data?.message ||
      "Unable to review evidence",
      "error"
    );
  }
};

  useEffect(() => {
    fetchDetail();
    fetchComments();
    fetchAuditTrail();
  }, [fetchDetail, fetchComments]);

  useEffect(() => {
    if (detail?.principle_code) {
      fetchSubmittedResponses();
    }
  }, [detail?.principle_code, fetchSubmittedResponses]);

  // ── Submit comment ────────────────────────────────────────────────────
  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await apiClient.post("/api/comments-submit", {
        submission_id,
        principle_id,
        comment_text: comment,
        comment_type: isIssue ? "issue" : "note",
      });
      setComment("");
      setIsIssue(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      fetchDetail();
      fetchComments();
      fetchAuditTrail();
    } catch (err) {
      console.error("Comment submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── After modal confirms — just refetch ───────────────────────────────
  const handleMarkReviewed = () => {
    fetchDetail();
    fetchComments();
    fetchAuditTrail();
  };

  const handleSendBackConfirm = () => {
    fetchDetail();
    fetchComments();
    fetchAuditTrail();
  };

  // ── Approve — POST /api/approve-submission ────────────────────────────
  const handleApprove = async () => {
    setActionLoading("approve");
    try {
      await apiClient.post("/api/approvesubmission", {
        submission_id,
        reporting_year_id: reportingYearId,
      });
      fetchDetail();
      fetchComments();
    } catch (err) {
      console.error("Approve error:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLock = async () => {
    setActionLoading("lock");
    try {
      await apiClient.post("/api/locksubmission", {
        submission_id,
        reporting_year_id: reportingYearId,
      });
      fetchDetail();
    } catch (err) {
      console.error("Lock error:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRework = async () => {
    setActionLoading("rework");
    try {
      await apiClient.post("/api/rejectsubmission", {
        submission_id,
        reporting_year_id: reportingYearId,
        principle_id,
      });
      fetchDetail();
    } catch (err) {
      console.error("Rework error:", err);
    } finally {
      setActionLoading(null);
    }
  };
const EvidencePanel = ({ evidence = [] }) => {

    if (!evidence.length) {
        return null;
    }

    return (
        <div className="va-layout__sidebar">

          <div className="va-sidebar-card">

            <div className="va-sidebar-card__title">

              <Icon name="fa-paperclip" />

              Attached Evidence

              <span className="va-comment-count">

                {evidence.length} files

              </span>

            </div>

            {!!evidence.length && (

              <div className="va-evidence-block">

  {evidence.map((file,index)=>(
<div key={index} className="va-evidence-card">

  {/* FILE NAME */}
  <a
    href={file.file_url}
    target="_blank"
    rel="noreferrer"
    className="va-evidence-link"
  >
    <div className="file_name_row">
      <i className="fa-regular fa-file-lines" />
      {file.file_name}
    </div>

    <div className="va-evidence-item__name">
      
    </div>
  </a>

  {/* QUESTION CODE */}
  <div className="va-evidence-question">
    {file.question_code}
  </div>

  {/* STATUS */}
  <div className="va-evidence-review">

  <span
    className={`sr-evidence-badge sr-evidence-${file.review_status || "pending"}`}
  >
    {(file.review_status || "pending").toUpperCase()}
  </span>

  {file.review_status !== "pending" &&
    file.reviewed_by_name && (

      <span className="va-evidence-review-meta">

        {" "}by{" "}
        <strong>
          {file.reviewed_by_name}
        </strong>

        {" "}on{" "}
        {formatDateTime(file.reviewed_at)}

      </span>

  )}

</div>

  {/* REVIEWED BY */}
  

  {/* COMMENT */}
  {file.review_comment && (
    <div className="va-evidence-comment">
      {file.review_comment}
    </div>
  )}

</div>

  ))}

</div>

            )}

            {!evidence.length && (

              <div
                className="va-state va-state--empty"
                style={{
                  padding:"16px"
                }}
              >

                No evidence attached.

              </div>

            )}

            <div
              className="va-heading__meta"
              style={{
                padding:"12px"
              }}
            >

              <span className="va-heading__readonly">

                <Icon name="fa-circle-check" />

                Evidence references are linked from the Audit Repository. Read-only.

              </span>

            </div>

          </div>

        </div>
    );
};
 const handleActionClick = (a) => {
  const label   = (a.label ?? "").toLowerCase().trim();
  const codeKey = (a.action_code ?? "").toLowerCase().trim();

  if (label.includes("mark reviewed") || label.includes("mark review")) {
    setActiveModalApiUrl("/api/submit-review");
  } else if (codeKey.includes("approve") || label.includes("approve")) {
    setActiveModalApiUrl("/api/approvesubmission");
  } else if (codeKey.includes("lock") || label.includes("lock")) {
    setActiveModalApiUrl("/api/locksubmission");
  } else if (codeKey.includes("send_back") || label.includes("send back")) {
    setShowSendBackModal(true);
  } 
  else if (codeKey.includes("rework") || label.includes("rework")) {
    setActiveModalApiUrl("/api/rejectsubmission");
}
  else if (a.url) {
    navigate(a.url, {
      state: { selectedEntity, legalEntities, reportingYearId, reportingYear, principle_id, submission_id },
    });
  }
};

  const backConfig = {
    "submission-tracker": {
      path: "/dashboard/submission-tracker",
      text: "Back to Submission Tracker",
    },
    "review-screen": {
      path: "/dashboard/review-screen",
      text: "Back to Review & Validation",
    },
    "approval-screen": {
      path: "/dashboard/approval-lock",
      text: "Back to Approval Screen",
    },
    "section-c": {
      path: "/dashboard/section-c",
      text: "Back to Section C",
    },
    "principle-dashboard": {
      path: "/dashboard/principle-dashboard",
      text: "Back to Principle Dashboard",
    },
  };

  const handleBack = () => {
    const target = backConfig[sourcePage] || backConfig["review-screen"];
    navigate(target.path, {
      state: { selectedEntity, legalEntities, reportingYearId, reportingYear },
    });
  };

  const bgColor    = detail?.color_code ?? "#f3f4f6";
  const fgColor    = detail?.fore_color  ?? "#374151";
  const statusCode = (detail?.status_code ?? "").toLowerCase();
  const isDraft    = statusCode === "draft" || statusCode === "not_started";

  const uniqueHistory = history;

  return (
    <div className="va-page">

      {/* Breadcrumb */}
      <div className="va-breadcrumb">
        <div className="va-breadcrumb__left">
          {detail?.principle_code && (
            <span className="va-crumb-code">{detail.principle_code}</span>
          )}
          <span className="va-crumb-sep">·</span>
          <span className="va-crumb-text">
            <div className="principle-meta-item">
              <span className="principle-meta-label">Reporting Year:</span>
              <span className="principle-meta-value">{detail?.year_label || reportingYear || "N/A"}</span>
            </div>
          </span>
          <div className="principle-meta-item">
            <span className="principle-meta-label">Legal Entity:</span>
            <span className="principle-meta-value">{entityName || "N/A"}</span>
          </div>
        </div>
        {detail?.status_name && (
          <span
            className="va-status-pill"
            style={{ background: bgColor, color: fgColor, borderColor: bgColor }}
          >
            <i className={`fa-solid ${detail.icon ?? "fa-solid fa-circle"} status_icon_size`} />
            {detail.status_name}
          </span>
        )}
      </div>

      {/* Heading */}
      <div className="va-heading">
        <h1 className="va-heading__title">
          {detail
            ? `${detail.principle_code} – ${detail.principle_name ?? section_name}`
            : section_name ?? "Loading…"}
        </h1>
        {detail?.description && <p className="va-heading__desc">{detail.description}</p>}
        <div className="va-heading__meta">
          {detail?.submitted_by && detail.submitted_by !== "-" && (
            <span>Submitted by <strong>{detail.submitted_by}</strong></span>
          )}
          {detail?.submitted_action_date && (
            <span> · Submitted on <strong>{detail.submitted_action_date}</strong></span>
          )}
          <span className="va-heading__readonly">
            <Icon name="fa-eye" /> Read-only review
          </span>
        </div>
      </div>

      {/* Banner */}
      {(!!detail?.banner_message || isDraft) && (
        <div className="va-notice">
          {detail?.banner_message ?? "Draft — Not Yet Submitted"}
        </div>
      )}

      {/* Stat cards */}
      {summary.length > 0 && (
        <div className="va-stats-row">
          {summary.map((s, i) => (
            <StatCard key={i} label={s.label} value={s.value} iconName={s.icon} color={s.color} />
          ))}
        </div>
      )}

      {/* Two-column layout */}
      <div className="va-layout">

        {/* LEFT — Disclosure Review */}
        <div className="va-layout__main">
          <div className="va-panel">
            <div className="va-panel__header">
              <span className="va-panel__title">Disclosure Review</span>
              <span className="va-panel__meta">Read-only · Submitted answers</span>
            </div>
            {loading && (
              <div className="va-state va-state--loading">
                <span className="va-spinner" /> Loading…
              </div>
            )}
            {error && !loading && (
              <div className="va-state va-state--error">⚠ {error}</div>
            )}
            {!loading && !error && (
              <div className="va-submitted-wrapper">
                {submittedResponses.map((question) => (

                    <div
                      key={question.question_id}
                      className="va-question-card"
                    >
<div className="ecf-indicator-type">{question.indicator_type}</div>
                      <div className="va-question-top">
                        <span className="va-q-code">
                          {question.question_code}
                        </span>
                      </div>

                      <h4 className="va-question-title">
                        {question.question_text}
                      </h4>

                      <ResponseViewRenderer
                        question={question}
                        allResponses={submittedResponses}
                        canReviewEvidence={canReviewEvidence}
                        onEvidenceReview={handleEvidenceReview}
                      />

                    </div>

                  ))}
              </div>
            )}
            {!loading && !error && sections.map((sec, i) => (
              <SectionAccordion
                key={sec.section_id ?? sec.section_name ?? i}
                section={sec}
                defaultOpen={i === 0}
              />
            ))}
          </div>
        </div>

        {/* RIGHT — Sidebar */}
        <div className="va-layout__sidebar">

        {/* MAIN EVIDENCE */}

        <EvidencePanel
    evidence={evidence}
/>

          {/* Reviewer Comments */}
          <div className="va-sidebar-card">
            <div className="va-sidebar-card__title">
              <Icon name="fa-comment-dots" /> Reviewer Comments
              {comments.length > 0 && (
                <span className="va-comment-count">{comments.length}</span>
              )}
            </div>

            {comments.length > 0 && (
              <div className="va-comment-history">

                {comments.map((c) => (
                  <CommentItem key={c.id} c={c} />
                ))}
              </div>
            )}
            {(!comments || comments.length === 0) && (
                <div
                  className="va-state va-state--empty"
                  style={{ padding: "16px" }}
                >
                  No comments yet.
                </div>
              )}
            {canComment && (
                <div className="va-comment-form">
                  <textarea
                    className="va-comment-ta"
                    placeholder="Add review comment for this principle…"
                    value={comment}
                    rows={3}
                    onChange={(e) => setComment(e.target.value)}
                  />
                 <div className="review-checkbox-row">
        <input className="matrix_checkbox"
          type="checkbox"
          checked={isIssue}
          onChange={(e) => setIsIssue(e.target.checked)}
        />
          <span>
            Needs Preparer's attention
          </span>
          
      </div>
        <p className={`text-xs mt-0.5 ${isIssue ? "text-red-500" : "text-gray-400"}`}>
            {isIssue
              ? "Preparer will see this as an open issue"
              : "Leave unchecked to save as a note"}
          </p>
      

                  <button
                    className={`va-comment-btn${saved ? " va-comment-btn--saved" : ""}`}
                    onClick={handleAddComment}
                    disabled={submitting || !comment.trim()}
                  >
                    {saved ? "✓ Saved" : submitting ? "Saving…" : "💬 Add Comment"}
                  </button>
                </div>
              )}

              
          </div>
          {/* Action Buttons */}
         {actions.length > 0 && (

              <div className="va-actions-card">

                {actions.map((a, i) => {

                  const labelKey =
                    (a.label ?? "")
                      .toLowerCase()
                      .trim();

                  const codeKey =
                    (a.action_code ?? "")
                      .toLowerCase()
                      .trim();

                  const isApprove =
                    codeKey.includes("approve") ||
                    labelKey.includes("approve");

                  const isLock =
                    codeKey.includes("lock") ||
                    labelKey.includes("lock");

                  const isRework =
                    codeKey.includes("rework") ||
                    labelKey.includes("rework");

                  const isLoading =
                    (isApprove &&
                      actionLoading === "approve")
                    ||
                    (isLock &&
                      actionLoading === "lock")
                    ||
                    (isRework &&
                      actionLoading === "rework");

                  return (

                    <button
                      key={i}
                      className="va-action-btn"
                      style={{
                        backgroundColor:
                          a.backcolor ??
                          "transparent",

                        color:
                          a.forecolor ??
                          "#374151",

                        borderColor:
                          a.forecolor ??
                          "#d1d5db"
                      }}
                      onClick={() =>
                        handleActionClick(a)
                      }
                      disabled={
                        isLoading ||
                        actionLoading !== null
                      }
                    >
                      {isLoading
                        ? <span className="va-action-btn__spinner" />
                        : <i className={`fa-solid ${a.icon}`} />
                      }

                      {isLoading
                        ? "Processing..."
                        : a.label}
                    </button>
                  );
                })}

              </div>
            )}

          {/* Workflow History */}
          <div className="va-sidebar-card">
            <div className="va-sidebar-card__title">Workflow History</div>
            {uniqueHistory.length === 0 ? (
              <p className="va-state va-state--empty">No history yet.</p>
            ) : (
              <div className="va-wf-list">
                {uniqueHistory.map((h, i) => (
                  <HistoryItem
                    key={`${h.brsr_status_id}-${i}`}
                    h={h}
                    isLast={i === uniqueHistory.length - 1}
                  />
                ))}
              </div>
            )}
          </div>
<AuditTrailPanel
    auditLogs={auditLogs}
    onViewDetails={
        openAuditDetails
    }
    Icon={Icon}
/>
        </div>
      </div>

      {/* Back button */}
      <button className="back_button" onClick={handleBack}>
        ← {backConfig[sourcePage]?.text || "Back"}
      </button>

      {/* Modals */}
      {showSendBackModal && (
        <SendBackModal
          onClose={() => setShowSendBackModal(false)}
          onConfirm={handleSendBackConfirm}
          submission_id={submission_id}
          reportingYearId={reportingYearId}
        />
      )}

        {activeModalApiUrl && (
        <MarkReviewedModal
          apiUrl={activeModalApiUrl}
          onClose={() => setActiveModalApiUrl(null)}
          onConfirm={() => { fetchDetail(); fetchComments(); }}
          submission_id={submission_id}
          reportingYearId={reportingYearId}
          principle_id={principle_id}
        />
      )}
<AuditDetailModal
    audit={
        showAuditModal
            ? selectedAudit
            : null
    }
    onClose={() => {

        setShowAuditModal(
            false
        );

        setSelectedAudit(
            null
        );

    }}
/>
    </div>
  );
};

export default ViewAction;